import { useSearchParams } from "react-router-dom";

import { useClinicalTeam, useClinicianSession } from "@/lib/api/clinicianHooks";
import { useBootstrap } from "@/lib/api/hooks";
import type { ClinicalTeamMember } from "@/types/clinician";

/** Explicit "every clinician" choice; sends no clinician_id. */
export const ALL_CLINICIANS = "all";
/** Worklist-only: work whose patient has no primary prescriber. */
export const UNASSIGNED = "unassigned";

export interface OwnerFilter {
  supervisor: boolean;
  /** Signed-in user's clinician id when they are an active prescriber. */
  ownClinicianId: string | null;
  /** Value for the owner select (the URL param, or the default). */
  selected: string;
  /** clinician_id to send to the API (undefined = backend default scope). */
  clinicianId: string | undefined;
  /** False until the default can be decided, so no all-clinician fetch runs first. */
  ready: boolean;
  showUnassigned: boolean;
  clinicians: ClinicalTeamMember[];
  select: (value: string) => void;
}

/**
 * Owner scope shared by the worklist, inbox and reports.
 *
 * The `clinician` URL param is the source of truth. Without one, a
 * supervisor who is also an active prescriber starts on their own patients
 * once team routing is on; "All clinicians" is an explicit choice. In
 * single-clinician mode, and for clinicians without supervisor access, the
 * previous behavior is unchanged (the backend scopes their lists).
 */
export function useOwnerFilter({
  allowUnassigned = false,
}: { allowUnassigned?: boolean } = {}): OwnerFilter {
  const [params, setParams] = useSearchParams();
  const bootstrap = useBootstrap();
  const supervisor =
    bootstrap.data?.admin_role === "admin" ||
    bootstrap.data?.admin_role === "clinical_supervisor";
  const team = useClinicalTeam(Boolean(supervisor));
  // The session carries the portal clinician id the team list uses;
  // bootstrap's patient id is the EMR's id and never matches it.
  const session = useClinicianSession();
  const teamMode = Boolean(team.data?.team_mode_enabled);
  const clinicians = team.data?.clinicians ?? [];
  const own = clinicians.find(
    (member) =>
      member.clinician_id === session.data?.clinician_id &&
      member.active &&
      member.provider_type === "prescriber",
  );
  const ownClinicianId = own?.clinician_id ?? null;
  const requested = params.get("clinician");
  const showUnassigned = allowUnassigned && teamMode;
  const selected =
    requested ||
    (supervisor && teamMode && ownClinicianId
      ? ownClinicianId
      : ALL_CLINICIANS);
  const clinicianId =
    !supervisor ||
    selected === ALL_CLINICIANS ||
    (selected === UNASSIGNED && !showUnassigned)
      ? undefined
      : selected;
  const ready =
    Boolean(requested) ||
    (!bootstrap.isLoading &&
      !(supervisor && (team.isLoading || session.isLoading)));

  return {
    supervisor,
    ownClinicianId,
    selected,
    clinicianId,
    ready,
    showUnassigned,
    clinicians,
    select: (value) => {
      const updated = new URLSearchParams(params);
      updated.set("clinician", value || ALL_CLINICIANS);
      setParams(updated, { replace: true });
    },
  };
}

/** Options for an owner select: mine, all, unassigned, then each clinician. */
export function OwnerOptions({ owner }: { owner: OwnerFilter }) {
  return (
    <>
      {owner.ownClinicianId && (
        <option value={owner.ownClinicianId}>My patients</option>
      )}
      <option value={ALL_CLINICIANS}>All clinicians</option>
      {owner.showUnassigned && (
        <option value={UNASSIGNED}>Unassigned (no licensed prescriber)</option>
      )}
      {owner.clinicians
        .filter(
          (member) =>
            member.active && member.clinician_id !== owner.ownClinicianId,
        )
        .map((member) => (
          <option key={member.clinician_id} value={member.clinician_id}>
            {member.display_name || member.email}
          </option>
        ))}
    </>
  );
}
